import {
  Alert,
  AlertTitle,
  Button,
  InputGroup,
} from "@clarkinc/zest-design-system/ui";
import { logMessage } from "@wss/error-tracking";
import React, { useRef, useState, useEffect } from "react";
import { valid } from "~/common/Email/EmailValidator";

interface Props {
  emailAddress: string;
  itemNumber: string;
  itemSelected: boolean;
}

const OutOfStock = ({ emailAddress, itemNumber, itemSelected }: Props) => {
  const alertRef = useRef<HTMLDivElement>(null);
  const form = useRef<HTMLFormElement>(null);

  const [emailSent, setEmailSent] = useState(false);

  const [email, setEmail] = useState("");

  useEffect(() => {
    setEmail(String(emailAddress));
  }, [emailAddress]);

  useEffect(() => {
    if (emailSent && alertRef.current) {
      alertRef.current.focus();
    }
  }, [emailSent]);

  const [errorMessage, setErrorMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const handleSendingEmail = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    if (!valid({ emailAddress: email })) {
      setIsError(true);
      return setErrorMessage("Email is invalid, try again.");
    }

    const apiUrl = "/api/customers/notifications/productavailability/add";

    const request = {
      itemnumber: itemNumber,
      email,
    };

    await fetch(apiUrl, {
      method: "POST",
      headers: {
        "Content-type": "application/json",
      },
      body: JSON.stringify(request),
    }).catch((error) => {
      logMessage(error, "info");
    });

    const { emailSignupEvent } = await import("~/gtm");
    emailSignupEvent(email);

    return setEmailSent(true);
  };

  return (
    <div className="add-to-cart">
      <div
        className="map"
        id="unavailableContainer"
        style={{ display: "block" }}
      >
        <input
          id="item_number"
          name="item_number"
          type="hidden"
          value={itemNumber}
        />
        <form
          action="#"
          className="emailForm"
          data-target="notifyMe"
          method="post"
          ref={form}
        >
          {!emailSent && (
            <InputGroup
              className="input-append mt-0"
              errorMessages={errorMessage}
              groupLabel="Email Address"
              hasError={isError}
              inputSize="medium"
              theme="wss"
            >
              <InputGroup.Label
                className="to-see-label notifyHeadingText"
                data-target="notifyMeHeading"
              >
                {itemSelected
                  ? "Notify me when this product is back in stock"
                  : "Select a product to be notified when it is back in stock"}
              </InputGroup.Label>
              <InputGroup.Wrapper>
                <InputGroup.Input
                  autoComplete="on"
                  className="emailField w-full"
                  data-testid="outOfStockNotifyEmail"
                  disabled={!itemSelected}
                  maxLength={100}
                  name="email"
                  type="email"
                  value={email}
                  onValueChange={(value) => {
                    setEmail(value);
                  }}
                />
                <Button
                  className="border border-solid border-gray-500 shadow-none"
                  color="gray"
                  data-testid="outOfStockNotifySubmit"
                  disabled={!itemSelected}
                  name="sendButton"
                  onClick={handleSendingEmail}
                  size="medium"
                  theme="wss"
                  type="submit"
                >
                  Send
                </Button>
              </InputGroup.Wrapper>
              <InputGroup.Error className="form__error" />
            </InputGroup>
          )}
        </form>
        <p className="messages hide" />
        <Alert
          className="w-auto mb-4"
          data-testid="notifySuccessMessage"
          isOpen={emailSent}
          ref={alertRef}
          theme="wss"
          variant="success"
          assertive
        >
          <AlertTitle className="text-xs leading-tight text-left text-gray-800">
            We will notify you when this product is available.
          </AlertTitle>
          <p className="text-xs leading-tight mb-0 text-left font-normal text-gray-800">
            To make sure you receive our email, please add{" "}
            <strong className="text-green-500 hover:text-green-700">
              help@webstaurantstore.com
            </strong>{" "}
            to your address book.
          </p>
        </Alert>
        <p className="mapText explainText">
          You will only be emailed once the product becomes available. We
          won&apos;t add you to our email list or share your data.
        </p>
      </div>
    </div>
  );
};

export default OutOfStock;
